import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import GoogleSignInButton from '../components/GoogleSignInButton';

function buildHostedUIUrl(provider: 'Google' | 'Facebook' = 'Google'): string {
  const base = (config.cognitoOAuthDomain || '').replace(/\/$/, '');
  const redirect = encodeURIComponent(config.cognitoOAuthRedirectSignIn || `${window.location.origin}/auth/callback`);
  const scope = encodeURIComponent('openid email profile');
  return `${base}/oauth2/authorize?identity_provider=${provider}&redirect_uri=${redirect}&response_type=CODE&client_id=${config.cognitoClientId}&scope=${scope}`;
}

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, state } = useAuth();
  const navigate = useNavigate();
  const useFederated = Boolean(config.cognitoOAuthDomain && config.cognitoClientId);

  if (state.status === 'authenticated') {
    navigate('/dashboard', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : typeof (err as { message?: string })?.message === 'string'
            ? (err as { message: string }).message
            : t('auth.signInFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 420, margin: '4rem auto', padding: '2rem' }}>
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1rem', color: '#111' }}>
        {t('auth.signIn')}
      </h1>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
        {t('auth.signInDesc')}
      </p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          {t('auth.email')}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          {t('auth.password')}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
            }}
          />
          <Link to="/forgot-password" style={{ marginTop: 4, fontSize: '0.85rem', color: '#0d9488', fontWeight: 500 }}>
            {t('auth.forgotPassword')}
          </Link>
        </label>
        {error && (
          <div style={{ padding: '0.75rem', background: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: '0.9rem' }}>
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '0.75rem 1.25rem',
            background: loading ? '#9ca3af' : '#0d9488',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: '1rem',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? t('auth.signingIn') : t('auth.signIn')}
        </button>
        {useFederated && (
          <>
            <p style={{ margin: '0.5rem 0 0', color: '#6b7280', fontSize: '0.85rem', textAlign: 'center' }}>— or —</p>
            <div style={{ display: 'block', width: '100%' }}>
              <GoogleSignInButton href={buildHostedUIUrl('Google')} label="Sign in with Google" style={{ width: '100%', justifyContent: 'center' }} />
            </div>
          </>
        )}
      </form>
      <p style={{ marginTop: '1.25rem', color: '#6b7280', fontSize: '0.9rem' }}>
        {t('auth.noAccount')} <Link to="/signup" style={{ color: '#0d9488', fontWeight: 600 }}>{t('auth.signUp')}</Link>
      </p>
      <p style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.9rem' }}>
        Need to verify your email? <Link to="/confirm" style={{ color: '#0d9488', fontWeight: 500 }}>Enter verification code</Link>
      </p>
      <Link to="/" style={{ display: 'inline-block', marginTop: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
        ← {t('auth.backToHome')}
      </Link>
    </div>
  );
}
